import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SEO_PAGES, SITE_URL, SIGNUP_PATH, EFFY_ORDER_PRODUCT } from './seoPages';

const OG_IMAGE_URL = `${SITE_URL}/logo/og-image.png`;

// Helmet tags shared by every SEO page: title, description, canonical, Open Graph,
// breadcrumb and (optionally) FAQ structured data. `faqs` must also be rendered
// visibly on the page — Google ignores FAQ markup for hidden content.
// `product` ({ name, path }) inserts the product level: Home › Effy Order › page.
export function SeoHead({ path, title, description, breadcrumbName, product, faqs, extraSchema }) {
  const url = `${SITE_URL}${path}`;
  const crumbs = [{ name: 'Home', item: `${SITE_URL}/` }];
  if (product) {
    crumbs.push({ name: product.name, item: `${SITE_URL}${product.path}` });
  }
  crumbs.push({ name: breadcrumbName, item: url });
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.item,
    })),
  };

  let faqSchema = null;
  if (faqs && faqs.length > 0) {
    faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
      })),
    };
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="EffyVenture" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={OG_IMAGE_URL} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE_URL} />
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      {extraSchema && <script type="application/ld+json">{JSON.stringify(extraSchema)}</script>}
    </Helmet>
  );
}

// Visible breadcrumb above the hero badge: "Effy Order › GST Billing Software".
// Tells visitors (and Google) which product a feature page belongs to.
export function ProductBreadcrumb({ product, current }) {
  return (
    <nav className="seo-breadcrumb" aria-label="Breadcrumb">
      <Link to={product.path} className="seo-breadcrumb__link">{product.name}</Link>
      <span className="seo-breadcrumb__sep" aria-hidden="true">›</span>
      <span className="seo-breadcrumb__current">{current}</span>
    </nav>
  );
}

// Long-form text for the guide pages: a list of { title, paragraphs } blocks.
export function GuideArticle({ guide }) {
  if (!guide || guide.length === 0) {
    return null;
  }
  return (
    <section className="seo-guide">
      <div className="seo-guide__inner">
        {guide.map((block) => (
          <div key={block.title} className="seo-guide__block">
            <h2 className="seo-guide__title">{block.title}</h2>
            {block.paragraphs.map((text) => (
              <p key={text} className="seo-guide__text">{text}</p>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

export function CheckIcon() {
  return (
    <svg className="oi-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function FaqSection({ faqs }) {
  if (!faqs || faqs.length === 0) {
    return null;
  }
  return (
    <section className="seo-faq">
      <div className="seo-faq__inner">
        <h2 className="seo-section-title">Frequently asked questions</h2>
        {faqs.map((faq) => (
          <details key={faq.question} className="seo-faq__item">
            <summary className="seo-faq__question">{faq.question}</summary>
            <p className="seo-faq__answer">{faq.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

// Internal links between the SEO pages — helps crawlers discover every page
// and passes relevance between related topics.
export function RelatedLinks({ paths }) {
  if (!paths || paths.length === 0) {
    return null;
  }
  const pages = paths
    .map((path) => SEO_PAGES.find((page) => page.path === path))
    .filter(Boolean);
  const hasCalculator = paths.includes('/tools/gst-calculator');

  return (
    <section className="seo-related">
      <div className="seo-related__inner">
        <h2 className="seo-section-title">Explore more Effy Order features</h2>
        <div className="seo-related__grid">
          {pages.map((page) => (
            <Link key={page.path} to={page.path} className="seo-related__card">
              <span className="seo-related__name">{page.badge}</span>
              <span className="seo-related__arrow" aria-hidden="true">→</span>
            </Link>
          ))}
          {hasCalculator && (
            <Link to="/tools/gst-calculator" className="seo-related__card">
              <span className="seo-related__name">Free GST Calculator</span>
              <span className="seo-related__arrow" aria-hidden="true">→</span>
            </Link>
          )}
          <Link to={EFFY_ORDER_PRODUCT.featuresPath} className="seo-related__card">
            <span className="seo-related__name">All Effy Order features</span>
            <span className="seo-related__arrow" aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

export function SignupCta() {
  return (
    <section className="oi-pricing-cta">
      <div className="oi-pricing-cta__inner">
        <h2 className="oi-pricing-cta__title">Start billing smarter today</h2>
        <p className="oi-pricing-cta__subtitle">
          Plans starting at ₹199/mo with a 30-day free trial. No hidden fees — upgrade anytime as you grow.
        </p>
        <div className="seo-cta__actions">
          <Link to={SIGNUP_PATH} className="btn-v2 btn-v2--primary">Try it free</Link>
          <Link to="/effy-order-pricing" className="btn-v2 btn-v2--secondary">View pricing</Link>
        </div>
      </div>
    </section>
  );
}
